/**
 * Pure validation functions for inline field validation.
 * Used by both blur (per-field) and submission (validateAll) validation paths.
 */

/**
 * Parses a date string into year/month/day components without timezone conversion.
 * Supports: yyyy-MM-dd (ISO date-only), M/d/yyyy, M/d/yy
 * Returns null if the string cannot be parsed.
 */
function parseDateComponents(val: string): { year: number; month: number; day: number } | null {
  const trimmed = val.trim();
  if (!trimmed) return null;

  let year: number, month: number, day: number;

  // Try yyyy-MM-dd (ISO date-only) — extract components directly, do NOT use new Date()
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else {
    // Try M/d/yyyy or M/d/yy
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      year = Number(slashMatch[3]);
      if (year < 100) year += 2000;
      month = Number(slashMatch[1]);
      day = Number(slashMatch[2]);
    } else {
      return null;
    }
  }

  // Validate that month/day form a real calendar date
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const check = new Date(year, month - 1, day);
  if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) {
    return null;
  }

  return { year, month, day };
}

/**
 * Compares two date component objects.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 */
function compareDateComponents(
  a: { year: number; month: number; day: number },
  b: { year: number; month: number; day: number }
): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/**
 * Validates that a required field has a non-empty trimmed value.
 * Returns "{label} is required." if trimmed value is empty, else undefined.
 */
export function validateRequired(value: string, label: string): string | undefined {
  return value.trim() === '' ? `${label} is required.` : undefined;
}

/**
 * Validates End Date conditional requirement.
 * Returns error if endDate is empty AND both noOfPeriods and typeOfPeriod are empty.
 */
export function validateEndDateConditional(
  endDate: string,
  noOfPeriods: string,
  typeOfPeriod: string
): string | undefined {
  if (endDate.trim() === '' && noOfPeriods.trim() === '' && typeOfPeriod.trim() === '') {
    return 'End Date is required when No of Periods and Type of Period are not provided.';
  }
  return undefined;
}

/**
 * Validates that flight end date is not before flight start date.
 * End date equal to start date is allowed (e.g., single-period flights).
 * - If either date is empty or unparseable → undefined (valid, skip check)
 * - If endDate < startDate → error message
 * - Otherwise → undefined (valid)
 */
export function validateEndDateNotBeforeStartDate(
  endDate: string,
  startDate: string
): string | undefined {
  if (!endDate.trim() || !startDate.trim()) return undefined;

  const end = parseDateComponents(endDate);
  const start = parseDateComponents(startDate);

  if (!end || !start) return undefined;

  if (compareDateComponents(end, start) < 0) {
    return 'End date cannot be before start date.';
  }
  return undefined;
}

/**
 * Validates quantity field.
 * - Empty → undefined (valid, qty is optional)
 * - Non-numeric → "Quantity must be a valid number."
 * - < 1 → "Quantity must be at least 1."
 * - ≥ 1 → undefined (valid)
 */
export function validateQuantity(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const num = Number(trimmed);
  if (isNaN(num)) return 'Quantity must be a valid number.';
  if (num < 1) return 'Quantity must be at least 1.';
  return undefined;
}

/**
 * Validates that exactly one of Unit ID / Product ID or Coverage is provided.
 * Returns error if both are empty OR both are provided, else undefined.
 */
export function validateProductCoverageMutual(
  unitId: string,
  coverage: string
): string | undefined {
  const hasUnitId = unitId.trim() !== '';
  const hasCoverage = coverage.trim() !== '';
  
  if (!hasUnitId && !hasCoverage) {
    return 'Either Unit ID / Product ID or Coverage is required.';
  }
  if (hasUnitId && hasCoverage) {
    return 'Cannot provide both Unit/Product ID and Coverage. Please provide only one.';
  }
  return undefined;
}

/**
 * Validates that hold expiration date is before flight start date.
 * - If either date is empty or unparseable → undefined (valid, skip check)
 * - If expiration >= flightStart → error message
 * - Otherwise → undefined (valid)
 */
export function validateExpirationBeforeFlightStart(
  holdExpirationDate: string,
  flightStartDate: string
): string | undefined {
  if (!holdExpirationDate.trim() || !flightStartDate.trim()) return undefined;

  const exp = parseDateComponents(holdExpirationDate);
  const start = parseDateComponents(flightStartDate);

  if (!exp || !start) return undefined;

  if (compareDateComponents(exp, start) >= 0) {
    return 'Hold expiration date must be before flight start date.';
  }
  return undefined;
}
